import { useEffect, useState } from 'react';
import type { Ref } from 'react';
import { InputText } from 'primereact/inputtext';

interface TemplateListInputProps {
  id: string;
  /** Handed to the input so a failed submit can focus it. */
  inputRef?: Ref<HTMLInputElement>;
  value: string[];
  disabled: boolean;
  describedBy?: string;
  invalid: boolean;
  onChange: (next: string[]) => void;
  onBlur: () => void;
}

/**
 * A multi-value field, typed as a comma-separated list.
 *
 * A template stores no option list, so there is nothing to pick from; the typed
 * text is kept locally so a trailing ", " survives while the person is still
 * typing, and the value handed to the form is always the parsed list.
 */
export function TemplateListInput({ id, inputRef, value, disabled, describedBy, invalid, onChange, onBlur }: TemplateListInputProps) {
  const [text, setText] = useState(value.join(', '));

  /* A reset from outside (Cancel, a reload) replaces what is typed. */
  useEffect(() => {
    setText((current) => {
      const parsed = current
        .split(',')
        .map((entry) => entry.trim())
        .filter((entry) => entry !== '');
      return parsed.join('|') === value.join('|') ? current : value.join(', ');
    });
  }, [value]);

  return (
    <InputText
      ref={inputRef}
      id={id}
      type="text"
      value={text}
      disabled={disabled}
      placeholder="Enter values, separated by commas"
      aria-describedby={describedBy}
      aria-invalid={invalid}
      className={invalid ? 'p-invalid' : undefined}
      onBlur={onBlur}
      onChange={(event) => {
        setText(event.target.value);
        onChange(
          event.target.value
            .split(',')
            .map((entry) => entry.trim())
            .filter((entry) => entry !== ''),
        );
      }}
    />
  );
}
